import { countLabel } from 'utils/ru';
import { formatDate as localeDate } from 'i18n/format';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CableIcon from '@mui/icons-material/Cable';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useConnections, useDeleteConnection } from 'api/connectors';
import { extractApiError } from 'api/errors';
import StatusBadge from 'components/common/StatusBadge';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';

import { useTranslation } from 'react-i18next';
import { localizeConnectorType, translateMetadata } from 'i18n/metadata';

const CATEGORY_COLOR = {
  database:  'primary',
  saas:      'secondary',
  file:      'default',
  streaming: 'warning',
};

function formatDate(iso) {
  if (!iso) return '—';
  return localeDate(new Date(iso), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function ConnectorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: connections, isLoading, error } = useConnections();
  const deleteConnection = useDeleteConnection();
  const [deletingId, setDeletingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeletingId(toDelete.public_id);
    setDeleteError(null);
    try {
      await deleteConnection.mutateAsync(toDelete.public_id);
      setToDelete(null);
    } catch (err) {
      setDeleteError(extractApiError(err, t('data.failedToDeleteConnectionPleaseTryAgain')));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CableIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>{t('data.connectors')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AccountCircleIcon />}
            onClick={() => navigate('/console/connectors/accounts')}
          >{t('data.connectedAccounts')}</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/console/connectors/new')}
          >{t('data.addConnection')}</Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('data.failedToLoadConnections')}</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : connections?.length === 0 ? (
        <EmptyState onAdd={() => navigate('/console/connectors/new')} />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>{t('data.name')}</TableCell>
                <TableCell>{t('data.type')}</TableCell>
                <TableCell>{t('data.category')}</TableCell>
                <TableCell>{t('data.status')}</TableCell>
                <TableCell>{t('data.lastTested')}</TableCell>
                <TableCell align="right">{t('data.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections?.map((conn) => (
                <TableRow
                  key={conn.public_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/console/connectors/${conn.public_id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {conn.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{localizeConnectorType(conn.connector_type).name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={translateMetadata(conn.connector_type.category)}
                      size="small"
                      color={CATEGORY_COLOR[conn.connector_type.category] || 'default'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={conn.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(conn.last_tested_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={t('data.open')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/console/connectors/${conn.public_id}`)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('data.delete2')}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === conn.public_id}
                        onClick={() => { setDeleteError(null); setToDelete(conn); }}
                      >
                        {deletingId === conn.public_id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmActionDialog
        open={!!toDelete}
        title={toDelete?.pipeline_count ? t('data.canTDeleteConnection') : t('data.deleteConnection')}
        confirmWord={t('data.delete')}
        confirmLabel={t('data.deleteConnection')}
        confirmColor="error"
        blocked={!!toDelete?.pipeline_count}
        loading={!!toDelete && deletingId === toDelete.public_id}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      >
        {toDelete?.pipeline_count ? (
          <Typography variant="body2">
            <strong>{toDelete?.name}</strong>{' '}{t('data.isUsedBy2')}{' '}{countLabel(toDelete.pipeline_count, 'pipeline')}{' '}{t('data.asASourceOrDestinationDelete')}{toDelete.pipeline_count === 1 ? t('data.thatPipeline') : t('data.thosePipelines')}{' '}{t('data.firstThenYouCanDeleteThisConnection')}</Typography>
        ) : (
          <Typography variant="body2">{t('data.thisPermanentlyDeletesTheConnection')}{' '}<strong>{toDelete?.name}</strong>{' '}{t('data.andItsStoredCredentialsThisActionCannotBeUndone')}</Typography>
        )}
      </ConfirmActionDialog>
    </Box>
  );
}

function EmptyState({ onAdd }) {
  const { t } = useTranslation();
  return (
    <Paper
      elevation={0}
      sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}
    >
      <CableIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" fontWeight={600} gutterBottom>{t('data.noConnectionsYet')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('data.connectYourFirstDataSourceToStartBuildingPipelines')}</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>{t('data.addConnection')}</Button>
    </Paper>
  );
}
